// MMO 공유 필드 연결 수명(REBIRTH-MODES-SPEC 4.2, 통합 2026-10-28). main.ts가 프레임마다 sync()를 부른다.
// - 2차 환생(MMORPG 모드) 영웅이 필드 장면에 있으면 FieldNet을 만들어 game.field.net에 꽂고 연결한다. 나가면 끊고 뺀다.
// - ?mmo=1(QA·개발)이면 모드와 상관없이 필드에서 연결한다. ?fieldws=ws://…면 그 서버로(QA 서버 tools/qa/field-server.mjs).
// - 월드 보스: 서버 권위 체력. 이 판 피해는 영웅의 공격·스킬이 시작될 때(행동 번호가 바뀌는 틱) 보스 사거리 안이면 가안 공식으로 보낸다.
//   (필드 시뮬에 보스 개체가 들어오면 실제 판정 피해로 바꾼다 — 스펙 4.4 남은 일)
// 프레임 경로에서 새 객체를 만들지 않는다(연결을 만들 때 한 번만).
import { REBIRTH } from '../shared/content/items';
import { BossPhase, FIELD_BOSS } from '../shared/fieldNet';
import { HeroAct, Scene, type GameView } from '../shared/types';
import { FieldNet, type FieldNetOptions } from './fieldNet';
import type { FieldNetHook } from './field/netHook';

/** sync가 읽고 쓰는 게임 부분(테스트는 가짜를 넣는다) */
export interface FieldNetHost {
  readonly view: GameView;
  readonly field: { net: FieldNetHook | null };
  /** 영웅을 그 자리로 옮긴다(게임의 정상 경로 Game.snapHero). 없으면 좌표 반영을 건너뛴다 */
  snapHero?(x: number, z: number): void;
}

export interface FieldNetLinkOptions {
  /** 모드와 상관없이 필드에서 연결(QA) */
  force?: boolean;
  /** 접속 주소(QA 서버). 없으면 같은 출처 */
  url?: string;
  /** FieldNet 만들기(테스트) */
  create?: (o: FieldNetOptions) => FieldNet;
}

const HERO_NAME: Readonly<Record<string, string>> = { kael: '카엘', lyra: '라이라', seren: '세렌', brann: '브란' };

/** 가안: 공격 한 번이 월드 보스에 주는 피해(레벨·회차 비례). 서버가 상한·빈도를 다시 검사한다 */
export function bossHitEstimate(level: number, cycle: number, skill: boolean): number {
  const base = 400 + Math.max(1, level) * 60;
  return Math.round(base * (1 + 0.15 * Math.max(0, cycle)) * (skill ? 2.2 : 1));
}

/**
 * MMO 모드로 연결해도 되는가: 저장이 MMO 모드(2차 환생)이고, 운영 스위치(REBIRTH 표 cycle 2의 released)가 켜졌거나 미리보기(`?modes=1`)일 때만.
 * 저장 cycle만 보면 저장을 손보거나 미공개 모드가 새어 연결된다. 틱마다 불러도 할당이 없다(for 루프, 클로저 없음)
 */
export function mmoMode(view: GameView): boolean {
  const r = view.rebirth;
  if (!r || r.modeId !== 'mmo') return false;
  if (r.modes) return true;
  for (let i = 0; i < REBIRTH.length; i++) if (REBIRTH[i].cycle === 2) return REBIRTH[i].released;
  return false;
}

export class FieldNetLink {
  net: FieldNet | null = null;
  /** 보스에 보낸 타격 수(QA) */
  hits = 0;
  private lastAct = -1;
  private lastActTick = -1;
  private seenSpawn = 0;
  private seenCorr = 0;

  constructor(private readonly host: FieldNetHost, private readonly o: FieldNetLinkOptions = {}) {}

  /** 지금 연결해야 하는가 */
  wanted(): boolean {
    const v = this.host.view;
    return v.flow.scene === Scene.Field && (this.o.force === true || mmoMode(v));
  }

  /** 프레임마다 한 번 */
  sync(): void {
    const want = this.wanted();
    if (want && !this.net) this.open();
    else if (!want && this.net) this.close();
    if (this.net) { this.applyServerPos(this.net); this.bossHits(this.net); }
  }

  /**
   * 서버가 정한 좌표를 영웅에 적용: Welcome 시작 좌표(계정 마지막 위치·입구)와 Correct(속도 위반 등으로 되돌린 자리).
   * 옮긴 뒤에야 pos를 보내므로(holdPos) 서버가 옛 좌표 때문에 reject하지 않는다. 할당 없음
   */
  private applyServerPos(net: FieldNet): void {
    const snap = this.host.snapHero;
    if (net.spawnSerial !== this.seenSpawn) {
      this.seenSpawn = net.spawnSerial;
      if (snap) snap.call(this.host, net.spawnX, net.spawnZ);
      net.ackSpawn();
    }
    const c = net.correction;
    if (c.serial !== this.seenCorr) {
      this.seenCorr = c.serial;
      if (snap) snap.call(this.host, c.x, c.z);
    }
  }

  private open(): void {
    const v = this.host.view;
    const heroId = v.heroId ?? 'kael';
    const cycle = v.rebirth?.cycle ?? 0;
    const opts: FieldNetOptions = { name: HERO_NAME[heroId] ?? '모험가', hero: heroId === 'lyra' ? 1 : 0, cycle, look: 0x11, url: this.o.url, holdPos: true };
    const net = this.o.create ? this.o.create(opts) : new FieldNet(opts);
    net.update(v.hero);
    net.start();
    this.net = net;
    this.host.field.net = net;
    this.lastAct = -1; this.lastActTick = -1;
    this.seenSpawn = net.spawnSerial; this.seenCorr = net.correction.serial;
  }

  private close(): void {
    this.net?.stop();
    this.net = null;
    if (this.host.field.net) this.host.field.net = null;
  }

  /** 공격·스킬이 새로 시작된 틱이면 사거리 안의 살아 있는 보스에 피해를 보낸다 */
  private bossHits(net: FieldNet): void {
    const h = this.host.view.hero;
    const started = h.act !== this.lastAct || h.actTick < this.lastActTick;
    this.lastAct = h.act; this.lastActTick = h.actTick;
    if (!started) return;
    const skill = h.act === HeroAct.Skill || h.act === HeroAct.Awaken;
    if (h.act !== HeroAct.Attack && !skill) return;
    const b = net.boss;
    if (b.phase !== BossPhase.Alive) return;
    const dx = h.x - b.x, dz = h.z - b.z;
    if (dx * dx + dz * dz > FIELD_BOSS.range * FIELD_BOSS.range) return;
    net.hitBoss(bossHitEstimate(h.level, this.host.view.rebirth?.cycle ?? 0, skill));
    this.hits++;
  }

  dispose(): void { this.close(); }
}
